import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { useCurrency } from '../../../../../context/CurrencyContext';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type BudgetPeriodOption = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  currency: string;
};

function ReportsPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const { authorizedFetch, auth } = useAuth();
  const { currency, formatPriceFrom } = useCurrency();

  const [periods, setPeriods] = useState<BudgetPeriodOption[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [loadingPeriods, setLoadingPeriods] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sections, setSections] = useState({
    summary: true,
    entries: true,
    purchases: false,
    losses: false,
    other: false,
  });
  const [reportLoading, setReportLoading] = useState(false);
  const [reportMessage, setReportMessage] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [toEmail, setToEmail] = useState(auth.email || '');
  const [cc, setCc] = useState('');
  const [message, setMessage] = useState('');
  const [showEmailOptions, setShowEmailOptions] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoadingPeriods(true);
      setError(null);
      try {
        const res = await authorizedFetch(
          `${API_BASE}/budget/periods?page=1&pageSize=50`,
        );
        if (!res.ok) {
          throw new Error('Failed');
        }
        const data = await res.json();
        setPeriods(
          (data.items ?? []).map((p: any) => ({
            id: p.id,
            name: p.name,
            startDate: p.startDate,
            endDate: p.endDate,
            currency: p.currency,
          })),
        );
      } catch {
        setError(
          t(
            'admin.reports_load_error',
            'Unable to load budget periods for reports.',
          ),
        );
      } finally {
        setLoadingPeriods(false);
      }
    };
    void load();
  }, [authorizedFetch, t]);

  const currentPeriod =
    periods.find((p) => p.id === selectedPeriodId) ?? null;

  const sectionList = [
    sections.summary && 'SUMMARY',
    sections.entries && 'ENTRIES',
    sections.purchases && 'PURCHASES_ONLY',
    sections.losses && 'LOSSES_ONLY',
    sections.other && 'OTHER_EXPENSES_ONLY',
  ].filter(Boolean);

  const canGenerate = !!selectedPeriodId && sectionList.length > 0;

  const callReport = async (mode: 'download' | 'email') => {
    if (!selectedPeriodId || reportLoading) return;
    setReportLoading(true);
    setReportError(null);
    setReportMessage(null);
    try {
      const res = await authorizedFetch(
        `${API_BASE}/budget/periods/${selectedPeriodId}/report`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sections: sectionList,
            sendByEmail: mode === 'email',
            toEmail: mode === 'email' ? toEmail || undefined : undefined,
            ccEmails:
              mode === 'email' && cc.trim()
                ? cc
                    .split(',')
                    .map((e) => e.trim())
                    .filter(Boolean)
                : undefined,
            message: mode === 'email' ? message || undefined : undefined,
          }),
        },
      );
      if (!res.ok) {
        throw new Error('Failed');
      }
      if (mode === 'email') {
        setReportMessage(
          t(
            'admin.budget_report_success_email',
            'Report will be sent to your email shortly.',
          ),
        );
        return;
      }
      const data = (await res.json()) as {
        ok: boolean;
        data?: string;
        filename?: string;
        contentType?: string;
      };
      if (!data.ok || !data.data || !data.filename) {
        throw new Error('Failed');
      }
      const binary = window.atob(data.data);
      const len = binary.length;
      const buffer = new Uint8Array(len);
      for (let i = 0; i < len; i += 1) {
        buffer[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([buffer], {
        type: data.contentType || 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setReportMessage(
        t(
          'admin.budget_report_success_download',
          'Report ready. Your download should start automatically.',
        ),
      );
    } catch {
      setReportError(
        t(
          'admin.budget_report_error',
          'Unable to generate report.',
        ),
      );
    } finally {
      setReportLoading(false);
    }
  };

  void location;
  void formatPriceFrom;

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.reports_title', 'Budget Reporting Center')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t(
              'admin.reports_subtitle',
              'Generate and share professional budget performance reports with clear financial and department context.',
            )}
          </p>
        </section>

        {error && (
          <p className="text-xs text-red-500">{error}</p>
        )}

        <section className="workspace-panel">
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Selected budget period</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{currentPeriod?.name || 'None'}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Report sections enabled</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{sectionList.length}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Report delivery mode</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{showEmailOptions ? 'Email distribution' : 'Direct PDF download'}</div>
            </div>
          </div>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-700">
                {t(
                  'admin.reports_period_label',
                  'Budget period',
                )}
              </label>
              {loadingPeriods ? (
                <p className="text-xs text-slate-500">
                  {t(
                    'admin.reports_period_loading',
                    'Loading budget periods…',
                  )}
                </p>
              ) : periods.length === 0 ? (
                <p className="text-xs text-slate-500">
                  {t(
                    'admin.reports_period_empty',
                    'No budget periods found. Create one first from the Budget section.',
                  )}
                </p>
              ) : (
                <select
                  value={selectedPeriodId}
                  onChange={(e) => setSelectedPeriodId(e.target.value)}
                  className="w-full max-w-xs rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                >
                  <option value="">
                    {t(
                      'admin.reports_period_placeholder',
                      'Select a budget period…',
                    )}
                  </option>
                  {periods.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} –{' '}
                      {new Date(p.startDate).toLocaleDateString()} ·{' '}
                      {new Date(p.endDate).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              )}
              {currentPeriod && (
                <p className="text-[11px] text-slate-500">
                  {t(
                    'admin.reports_period_currency_hint',
                    'Reports for this period will use currency',
                  )}{' '}
                  <span className="font-semibold text-slate-700">
                    {currentPeriod.currency || currency}
                  </span>
                  .
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2 text-xs text-slate-700">
              <span className="font-semibold">
                {t(
                  'admin.reports_sections_label',
                  'Include sections',
                )}
              </span>
              <div className="flex flex-wrap gap-3">
                <label className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={sections.summary}
                    onChange={(e) =>
                      setSections((prev) => ({
                        ...prev,
                        summary: e.target.checked,
                      }))
                    }
                  />
                  {t('backoffice.budget_report_section_summary', 'Summary')}
                </label>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={sections.entries}
                    onChange={(e) =>
                      setSections((prev) => ({
                        ...prev,
                        entries: e.target.checked,
                      }))
                    }
                  />
                  {t(
                    'admin.budget_report_section_entries',
                    'All entries',
                  )}
                </label>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={sections.purchases}
                    onChange={(e) =>
                      setSections((prev) => ({
                        ...prev,
                        purchases: e.target.checked,
                      }))
                    }
                  />
                  {t(
                    'admin.budget_report_section_purchases',
                    'Purchases only',
                  )}
                </label>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={sections.losses}
                    onChange={(e) =>
                      setSections((prev) => ({
                        ...prev,
                        losses: e.target.checked,
                      }))
                    }
                  />
                  {t(
                    'admin.budget_report_section_losses',
                    'Losses only',
                  )}
                </label>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={sections.other}
                    onChange={(e) =>
                      setSections((prev) => ({
                        ...prev,
                        other: e.target.checked,
                      }))
                    }
                  />
                  {t(
                    'admin.budget_report_section_other',
                    'Other expenses & adjustments',
                  )}
                </label>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-4 border-t border-slate-100 pt-4 text-xs text-slate-700">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={!canGenerate || reportLoading}
                onClick={() => void callReport('download')}
                className="inline-flex items-center justify-center rounded-md bg-slate-800 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {reportLoading
                  ? t(
                      'admin.budget_report_generating',
                      'Generating report…',
                    )
                  : t(
                      'admin.budget_report_button_download',
                      'Download PDF',
                    )}
              </button>
              <button
                type="button"
                disabled={!canGenerate || reportLoading}
                onClick={() => {
                  if (!showEmailOptions) {
                    setShowEmailOptions(true);
                    return;
                  }
                  void callReport('email');
                }}
                className="inline-flex items-center justify-center rounded-md bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {reportLoading
                  ? t(
                      'admin.budget_report_generating',
                      'Generating report…',
                    )
                  : t(
                      'admin.budget_report_button_email',
                      showEmailOptions ? 'Send by email' : 'Send by email…',
                    )}
              </button>
              {!canGenerate && (
                <p className="text-[11px] text-slate-500">
                  {t(
                    'admin.reports_hint_select_period',
                    'Select a budget period and at least one section to enable report actions.',
                  )}
                </p>
              )}
            </div>

            {showEmailOptions && (
              <div className="grid gap-4 sm:grid-cols-[1.2fr,1fr]">
                <div className="space-y-3">
                  <p className="font-semibold text-slate-800">
                    {t(
                      'admin.reports_email_panel_title',
                      'Email delivery (optional)',
                    )}
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="space-y-1">
                      <label className="block text-[11px] font-medium text-slate-600">
                        {t('backoffice.reports_to_email_label', 'To')}
                      </label>
                      <input
                        type="email"
                        value={toEmail}
                        onChange={(e) => setToEmail(e.target.value)}
                        placeholder={auth.email || 'recipient@company.com'}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-[11px] font-medium text-slate-600">
                        {t('backoffice.reports_cc_label', 'CC (comma-separated)')}
                      </label>
                      <input
                        type="text"
                        value={cc}
                        onChange={(e) => setCc(e.target.value)}
                        placeholder="finance@company.com, owner@company.com"
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-[11px] font-medium text-slate-600">
                      {t(
                        'admin.reports_message_label',
                        'Custom message (included in email body)',
                      )}
                    </label>
                    <textarea
                      rows={3}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t(
                        'admin.reports_message_placeholder',
                        'Add optional context for the recipient about this budget report…',
                      )}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                    />
                  </div>
                  <p className="text-[11px] text-slate-500">
                    {t(
                      'admin.reports_email_hint',
                      'If no email is provided, the report will be sent to your own admin email address.',
                    )}
                  </p>
                </div>
                <div className="flex flex-col justify-end">
                  {reportError && (
                    <p className="mt-1 text-[11px] text-red-500">
                      {reportError}
                    </p>
                  )}
                  {reportMessage && (
                    <p className="mt-1 text-[11px] text-emerald-600">
                      {reportMessage}
                    </p>
                  )}
                </div>
              </div>
            )}

            {!showEmailOptions && (reportError || reportMessage) && (
              <div>
                {reportError && (
                  <p className="mt-1 text-[11px] text-red-500">
                    {reportError}
                  </p>
                )}
                {reportMessage && (
                  <p className="mt-1 text-[11px] text-emerald-600">
                    {reportMessage}
                  </p>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
    </MainLayout>
  );
}

export default ReportsPage;

